import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import { isNil } from "lodash";
import { useCallback, useMemo } from "react";

import {
  type NotificationEngineMode,
  type NotificationEventType,
} from "../../generated/graphql";
import NotificationCutoverRow from "./notification-cutover-row";
import { useNotificationCutover } from "./use-notification-cutover";

const styles = {
  subtitle: { mt: 0.5, mb: 3, maxWidth: 760 },
  error: { mb: 2, maxWidth: 760 },
  loading: { display: "flex", justifyContent: "center", p: 4 },
  table: { maxWidth: 1100 },
  toggleHeader: { width: 48 },
} satisfies Record<string, SxProps<Theme>>;

/**
 * Internal tool to flip the notification engine's per-event cutover mode for
 * the current environment. Each environment reads and writes its own database,
 * so this page configures only the environment it is served from (dev, staging,
 * release, or prod).
 *
 * Each row expands into what the event means, who it reaches, on which
 * channels, where else it is switched on or off, and whether any of that
 * results in a delivery today, so a flip is not made against a bare event
 * identifier. All of that is static metadata from the event registry; no live
 * recipient is resolved.
 */
const NotificationCutover = () => {
  const { configs, loading, error, saving, saveError, changeMode } =
    useNotificationCutover();

  // Every event is already on this page, so the partner precondition resolves
  // against a row in hand rather than a second query.
  const modeByEventType = useMemo(
    () => new Map(configs.map((one) => [one.eventType, one.mode])),
    [configs],
  );

  const handleChange = useCallback(
    (eventType: NotificationEventType, mode: NotificationEngineMode) => {
      void changeMode(eventType, mode);
    },
    [changeMode],
  );

  return (
    <Box>
      <Typography variant="h5">Notification engine cutover</Typography>
      <Typography color="text.secondary" variant="body2" sx={styles.subtitle}>
        Flip each notification event between the legacy path and the new engine
        for this environment only. Off keeps the legacy code sending. Shadow
        runs the engine in parallel and compares its output without sending.
        Full hands delivery to the engine. Expand a row before flipping it: an
        event can be delivering today from the legacy path while its mode reads
        Off, and Full is unavailable for an event whose cutover work has not
        landed.
      </Typography>

      {error === undefined ? null : (
        <Alert severity="error" sx={styles.error}>
          {error.message}
        </Alert>
      )}
      {saveError === undefined ? null : (
        <Alert severity="error" sx={styles.error}>
          {saveError.message}
        </Alert>
      )}

      {loading ? (
        <Box sx={styles.loading}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={styles.table}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={styles.toggleHeader} />
                <TableCell>Event</TableCell>
                <TableCell>Reaches anyone</TableCell>
                <TableCell>Mode</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.map((config) => (
                <NotificationCutoverRow
                  key={config.eventType}
                  config={config}
                  saving={saving}
                  requiredPartnerMode={
                    isNil(config.requiredPartnerEvent)
                      ? null
                      : (modeByEventType.get(config.requiredPartnerEvent) ??
                        null)
                  }
                  onChangeMode={handleChange}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default NotificationCutover;
